import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  ArrowLeft, Download, Loader2, Plus, Store, Trash2, MapPin, Package,
  Truck, Save, X, Check, ChevronRight, ChevronDown, CalendarClock, RotateCcw,
} from "lucide-react";
import { api } from "@/lib/api";

const euros = (cents) => (Number(cents || 0) / 100).toFixed(2);
const toCents = (v) => Math.round(parseFloat(String(v).replace(",", ".")) * 100) || 0;
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString("fr-FR", { day: "numeric", month: "short", year: "numeric" }) : "—");
const todayISO = () => new Date().toISOString().slice(0, 10);

const STATUS = {
  prevue: { label: "Prévue", cls: "bg-amber-deep/10 text-amber-deep" },
  livree: { label: "Livrée", cls: "bg-sage/15 text-sage" },
};

export default function AdminStores() {
  const [stores, setStores] = useState(null);
  const [selected, setSelected] = useState(null); // store_id
  const [newName, setNewName] = useState("");
  const [newCity, setNewCity] = useState("");
  const [creating, setCreating] = useState(false);

  const loadStores = () => api.get("/admin/stores").then(({ data }) => setStores(data.stores));

  useEffect(() => {
    loadStores();
  }, []);

  const createStore = async () => {
    if (!newName.trim()) return toast.error("Indiquez le nom du magasin");
    setCreating(true);
    try {
      const { data } = await api.post("/admin/stores", { name: newName.trim(), city: newCity.trim() });
      toast.success("Magasin créé");
      setNewName("");
      setNewCity("");
      await loadStores();
      setSelected(data.store_id);
    } catch {
      toast.error("Création impossible");
    } finally {
      setCreating(false);
    }
  };

  const exportExcel = async () => {
    try {
      const res = await api.get("/admin/stores/export", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "magasins_petales_de_miel.xlsx";
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Export impossible");
    }
  };

  if (stores === null) {
    return (
      <div className="flex items-center justify-center gap-3 py-32 text-bark-soft">
        <Loader2 className="animate-spin text-amber-deep" size={26} />
        <span className="font-serif text-lg">Chargement…</span>
      </div>
    );
  }

  if (selected) {
    return <StoreDetail storeId={selected} onBack={() => { setSelected(null); loadStores(); }} />;
  }

  return (
    <div data-testid="admin-stores-page" className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-serif text-3xl font-semibold text-bark">Magasins revendeurs</h1>
          <p className="mt-1 text-sm text-bark-soft">
            Gestion indépendante de la boutique en ligne : stocks, prix et livraisons par point de vente. {stores.length} magasin(s).
          </p>
        </div>
        {stores.length > 0 && (
          <button
            data-testid="stores-export-button"
            onClick={exportExcel}
            className="flex items-center gap-2 rounded-full border border-amber-800/25 bg-white px-5 py-2.5 text-sm font-medium text-bark transition-colors hover:border-amber-deep hover:text-amber-deep"
          >
            <Download size={16} /> Exporter (Excel)
          </button>
        )}
      </div>

      {/* Nouveau magasin */}
      <div className="rounded-2xl border border-amber-800/15 bg-white p-5 shadow-sm">
        <p className="mb-3 flex items-center gap-2 text-sm font-semibold text-bark"><Plus size={16} /> Nouveau magasin</p>
        <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
          <label className="flex-1 text-xs font-medium uppercase tracking-wider text-stone-mute">
            Nom du magasin
            <input
              data-testid="store-new-name"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder="Ex. Épicerie du Village"
              className="mt-1 w-full rounded-xl border border-amber-800/20 px-3 py-2.5 text-sm text-bark outline-none focus:border-amber-deep"
            />
          </label>
          <label className="flex-1 text-xs font-medium uppercase tracking-wider text-stone-mute">
            Ville
            <input
              data-testid="store-new-city"
              value={newCity}
              onChange={(e) => setNewCity(e.target.value)}
              placeholder="Ex. Vizille"
              className="mt-1 w-full rounded-xl border border-amber-800/20 px-3 py-2.5 text-sm text-bark outline-none focus:border-amber-deep"
            />
          </label>
          <button
            data-testid="store-create-button"
            onClick={createStore}
            disabled={creating}
            className="flex items-center justify-center gap-2 rounded-full bg-amber-deep px-6 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-amber-deep/90 disabled:opacity-60"
          >
            {creating ? <Loader2 size={16} className="animate-spin" /> : <Plus size={16} />} Ajouter
          </button>
        </div>
      </div>

      {stores.length === 0 ? (
        <div data-testid="stores-empty" className="rounded-2xl border border-amber-800/15 bg-white p-10 text-center">
          <Store size={30} className="mx-auto text-stone-mute" />
          <p className="mt-3 font-serif text-xl text-bark">Aucun magasin pour l'instant</p>
          <p className="mt-1 text-sm text-bark-soft">Ajoutez votre premier point de vente ci-dessus.</p>
        </div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {stores.map((s) => (
            <button
              key={s.store_id}
              data-testid={`store-card-${s.store_id}`}
              onClick={() => setSelected(s.store_id)}
              className="group flex flex-col rounded-2xl border border-amber-800/15 bg-white p-5 text-left shadow-sm transition-all hover:border-amber-deep hover:shadow-md"
            >
              <div className="flex items-start justify-between">
                <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-amber-deep/10 text-amber-deep">
                  <Store size={20} />
                </div>
                <ChevronRight size={18} className="text-stone-mute transition-transform group-hover:translate-x-1 group-hover:text-amber-deep" />
              </div>
              <h2 className="mt-3 font-serif text-xl font-semibold text-bark">{s.name}</h2>
              <p className="mt-0.5 flex items-center gap-1 text-sm text-bark-soft"><MapPin size={13} /> {s.city || "—"}</p>
              <div className="mt-4 flex gap-4 text-sm">
                <span className="flex items-center gap-1.5 text-bark-soft"><Package size={14} /> {s.product_count} produit(s)</span>
                <span className="flex items-center gap-1.5 text-bark-soft">Stock {s.total_stock}</span>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function StoreDetail({ storeId, onBack }) {
  const [store, setStore] = useState(null);
  const [tab, setTab] = useState("products");
  const [name, setName] = useState("");
  const [city, setCity] = useState("");
  const [lines, setLines] = useState([]);
  const [saving, setSaving] = useState(false);
  const [deliveries, setDeliveries] = useState([]);

  const load = async () => {
    const { data } = await api.get(`/admin/stores/${storeId}`);
    setStore(data);
    setName(data.name);
    setCity(data.city);
    setLines(data.lines.map((l) => ({ ...l, priceStr: euros(l.price_cents) })));
  };
  const loadDeliveries = async () => {
    const { data } = await api.get(`/admin/stores/${storeId}/deliveries`);
    setDeliveries(data.deliveries);
  };

  useEffect(() => {
    load();
    loadDeliveries();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storeId]);

  const setLine = (pid, patch) => setLines((ls) => ls.map((l) => (l.product_id === pid ? { ...l, ...patch } : l)));

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/admin/stores/${storeId}`, {
        name: name.trim(),
        city: city.trim(),
        lines: lines.map((l) => ({
          product_id: l.product_id,
          active: !!l.active,
          price_cents: toCents(l.priceStr),
          stock: parseInt(l.stock, 10) || 0,
        })),
      });
      toast.success("Magasin enregistré");
      await load();
    } catch {
      toast.error("Enregistrement impossible");
    } finally {
      setSaving(false);
    }
  };

  const removeStore = async () => {
    if (!window.confirm("Supprimer définitivement ce magasin et son historique de livraisons ?")) return;
    try {
      await api.delete(`/admin/stores/${storeId}`);
      toast.success("Magasin supprimé");
      onBack();
    } catch {
      toast.error("Suppression impossible");
    }
  };

  const resetStock = async () => {
    if (!window.confirm("Remettre à zéro le stock de tous les produits distribués dans ce magasin ?")) return;
    try {
      await api.post(`/admin/stores/${storeId}/reset-stock`);
      toast.success("Stocks remis à zéro");
      await load();
    } catch {
      toast.error("Réinitialisation impossible");
    }
  };

  if (!store) {
    return (
      <div className="flex items-center justify-center gap-3 py-32 text-bark-soft">
        <Loader2 className="animate-spin text-amber-deep" size={26} />
        <span className="font-serif text-lg">Chargement…</span>
      </div>
    );
  }

  const activeCount = lines.filter((l) => l.active).length;

  return (
    <div data-testid="admin-store-detail" className="space-y-6">
      <button data-testid="store-back-button" onClick={onBack} className="flex items-center gap-2 text-sm font-medium text-bark-soft hover:text-amber-deep">
        <ArrowLeft size={16} /> Tous les magasins
      </button>

      {/* En-tête magasin */}
      <div className="rounded-2xl border border-amber-800/15 bg-white p-5 shadow-sm">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
          <div className="flex flex-1 flex-col gap-3 sm:flex-row">
            <label className="flex-1 text-xs font-medium uppercase tracking-wider text-stone-mute">
              Nom
              <input
                data-testid="store-edit-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="mt-1 w-full rounded-xl border border-amber-800/20 px-3 py-2.5 text-sm font-semibold text-bark outline-none focus:border-amber-deep"
              />
            </label>
            <label className="flex-1 text-xs font-medium uppercase tracking-wider text-stone-mute">
              Ville
              <input
                data-testid="store-edit-city"
                value={city}
                onChange={(e) => setCity(e.target.value)}
                className="mt-1 w-full rounded-xl border border-amber-800/20 px-3 py-2.5 text-sm text-bark outline-none focus:border-amber-deep"
              />
            </label>
          </div>
          <div className="flex gap-2">
            <button
              data-testid="store-reset-stock-button"
              onClick={resetStock}
              className="flex items-center gap-2 rounded-full border border-amber-800/25 bg-white px-4 py-2.5 text-sm font-medium text-bark transition-colors hover:border-amber-deep hover:text-amber-deep"
            >
              <RotateCcw size={15} /> Réinitialiser les stocks
            </button>
            <button
              data-testid="store-delete-button"
              onClick={removeStore}
              className="flex items-center gap-2 rounded-full border border-terra/30 px-4 py-2.5 text-sm font-medium text-terra transition-colors hover:bg-terra/10"
            >
              <Trash2 size={15} /> Supprimer
            </button>
          </div>
        </div>
      </div>

      {/* Onglets */}
      <div className="flex gap-2 border-b border-amber-900/10">
        <button
          data-testid="store-tab-products"
          onClick={() => setTab("products")}
          className={`flex items-center gap-2 border-b-2 px-4 py-2.5 text-sm font-medium transition-colors ${tab === "products" ? "border-amber-deep text-amber-deep" : "border-transparent text-bark-soft hover:text-bark"}`}
        >
          <Package size={16} /> Produits &amp; stock
        </button>
        <button
          data-testid="store-tab-deliveries"
          onClick={() => setTab("deliveries")}
          className={`flex items-center gap-2 border-b-2 px-4 py-2.5 text-sm font-medium transition-colors ${tab === "deliveries" ? "border-amber-deep text-amber-deep" : "border-transparent text-bark-soft hover:text-bark"}`}
        >
          <Truck size={16} /> Livraisons
        </button>
      </div>

      {tab === "products" ? (
        <ProductsTab
          lines={lines}
          setLine={setLine}
          save={save}
          saving={saving}
          activeCount={activeCount}
        />
      ) : (
        <DeliveriesTab
          storeId={storeId}
          catalog={lines}
          deliveries={deliveries}
          reload={async () => { await loadDeliveries(); await load(); }}
        />
      )}
    </div>
  );
}

function StoreLineRow({ l, setLine }) {
  return (
    <div
      data-testid={`store-line-${l.product_id}`}
      className={`flex flex-col gap-3 rounded-2xl border p-4 transition-colors sm:flex-row sm:items-center ${l.active ? "border-amber-deep/40 bg-white" : "border-amber-800/10 bg-white/70"}`}
    >
      <button
        data-testid={`store-line-toggle-${l.product_id}`}
        onClick={() => setLine(l.product_id, { active: !l.active })}
        className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border transition-colors ${l.active ? "border-amber-deep bg-amber-deep text-white" : "border-amber-800/25 bg-white text-transparent"}`}
        aria-label="Distribué ici"
      >
        <Check size={16} />
      </button>
      <div className="min-w-0 flex-1">
        <p className="truncate font-medium text-bark">
          {l.name}
          {l.weight && <span className="ml-2 rounded-md bg-sand px-1.5 py-0.5 text-xs font-semibold text-bark-soft">{l.weight}</span>}
        </p>
        <p className="text-xs text-stone-mute">Prix site : {euros(l.site_price_cents)} €</p>
      </div>
      {l.active && (
        <div className="flex items-center gap-3">
          <label className="text-xs font-medium uppercase tracking-wider text-stone-mute">
            Prix (€)
            <input
              data-testid={`store-line-price-${l.product_id}`}
              value={l.priceStr}
              onChange={(e) => setLine(l.product_id, { priceStr: e.target.value })}
              inputMode="decimal"
              className="mt-1 w-24 rounded-xl border border-amber-800/20 px-3 py-2 text-sm text-bark outline-none focus:border-amber-deep"
            />
          </label>
          <label className="text-xs font-medium uppercase tracking-wider text-stone-mute">
            Stock
            <input
              data-testid={`store-line-stock-${l.product_id}`}
              value={l.stock}
              onChange={(e) => setLine(l.product_id, { stock: e.target.value.replace(/[^0-9]/g, "") })}
              inputMode="numeric"
              className="mt-1 w-20 rounded-xl border border-amber-800/20 px-3 py-2 text-sm text-bark outline-none focus:border-amber-deep"
            />
          </label>
        </div>
      )}
    </div>
  );
}

function ProductsTab({ lines, setLine, save, saving, activeCount }) {
  const [openCats, setOpenCats] = useState({}); // cadre "non distribués" ouvert par catégorie

  const groups = useMemo(() => {
    const map = new Map();
    for (const l of lines) {
      const key = l.category_name || "Sans catégorie";
      if (!map.has(key)) map.set(key, { name: key, active: [], inactive: [] });
      map.get(key)[l.active ? "active" : "inactive"].push(l);
    }
    return [...map.values()].sort((a, b) => a.name.localeCompare(b.name, "fr"));
  }, [lines]);

  const toggleCat = (name) => setOpenCats((o) => ({ ...o, [name]: !o[name] }));

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-bark-soft">
          Cochez les produits distribués ici. Le prix est pré-rempli depuis le site, modifiable par magasin.
          <span className="ml-1 font-semibold text-bark">{activeCount} distribué(s).</span>
        </p>
        <button
          data-testid="store-save-products"
          onClick={save}
          disabled={saving}
          className="flex items-center gap-2 rounded-full bg-amber-deep px-6 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-amber-deep/90 disabled:opacity-60"
        >
          {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />} Enregistrer
        </button>
      </div>

      <div className="space-y-5">
        {groups.map((g) => {
          const open = !!openCats[g.name];
          return (
            <div key={g.name} data-testid={`store-cat-${g.name}`} className="space-y-2">
              <div className="flex items-center gap-2">
                <h3 className="font-serif text-lg font-semibold text-bark">{g.name}</h3>
                <span className="rounded-full bg-sand px-2.5 py-0.5 text-xs font-medium text-bark-soft">
                  {g.active.length}/{g.active.length + g.inactive.length}
                </span>
              </div>

              {g.active.map((l) => (
                <StoreLineRow key={l.product_id} l={l} setLine={setLine} />
              ))}

              {g.inactive.length > 0 && (
                <div className="rounded-2xl border border-dashed border-amber-800/25 bg-sand/40">
                  <button
                    data-testid={`store-cat-toggle-${g.name}`}
                    onClick={() => toggleCat(g.name)}
                    className="flex w-full items-center justify-between gap-2 px-4 py-3 text-left text-sm font-medium text-bark-soft transition-colors hover:text-amber-deep"
                  >
                    <span>{g.inactive.length} produit(s) non distribué(s)</span>
                    {open ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
                  </button>
                  {open && (
                    <div className="space-y-2 border-t border-amber-800/10 p-3">
                      {g.inactive.map((l) => (
                        <StoreLineRow key={l.product_id} l={l} setLine={setLine} />
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function DeliveriesTab({ storeId, catalog, deliveries, reload }) {
  const distributable = useMemo(() => catalog, [catalog]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null); // delivery_id or null (new)
  const [date, setDate] = useState(todayISO());
  const [status, setStatus] = useState("prevue");
  const [note, setNote] = useState("");
  const [items, setItems] = useState([]); // {product_id, qty}
  const [pickId, setPickId] = useState("");
  const [pickQty, setPickQty] = useState(1);
  const [busy, setBusy] = useState(false);

  const resetForm = () => {
    setEditing(null);
    setDate(todayISO());
    setStatus("prevue");
    setNote("");
    setItems([]);
    setPickId("");
    setPickQty(1);
  };

  const startNew = () => { resetForm(); setOpen(true); };
  const startEdit = (d) => {
    setEditing(d.delivery_id);
    setDate((d.date || "").slice(0, 10) || todayISO());
    setStatus(d.status || "prevue");
    setNote(d.note || "");
    setItems((d.items || []).map((it) => ({ product_id: it.product_id, qty: it.qty })));
    setOpen(true);
  };

  const addItem = () => {
    if (!pickId) return toast.error("Choisissez un produit");
    const qty = parseInt(pickQty, 10) || 1;
    setItems((its) => {
      const ex = its.find((i) => i.product_id === pickId);
      if (ex) return its.map((i) => (i.product_id === pickId ? { ...i, qty: i.qty + qty } : i));
      return [...its, { product_id: pickId, qty }];
    });
    setPickId("");
    setPickQty(1);
  };

  const nameOf = (pid, fallback) => {
    const c = catalog.find((x) => x.product_id === pid);
    const name = (c && c.name) || fallback || pid;
    const weight = c && c.weight;
    return weight ? `${name} - ${weight}` : name;
  };

  const setItemQty = (pid, val) => {
    const q = parseInt(String(val).replace(/[^0-9]/g, ""), 10) || 0;
    setItems((its) => its.map((i) => (i.product_id === pid ? { ...i, qty: q } : i)));
  };

  const submit = async () => {
    if (items.length === 0) return toast.error("Ajoutez au moins un produit");
    if (items.some((i) => (parseInt(i.qty, 10) || 0) < 1)) return toast.error("Chaque produit doit avoir une quantité d'au moins 1");
    setBusy(true);
    const body = { date, status, note, items: items.map((i) => ({ product_id: i.product_id, qty: parseInt(i.qty, 10) || 0 })) };
    try {
      if (editing) await api.put(`/admin/stores/deliveries/${editing}`, body);
      else await api.post(`/admin/stores/${storeId}/deliveries`, body);
      toast.success(editing ? "Livraison mise à jour" : "Livraison enregistrée");
      setOpen(false);
      resetForm();
      await reload();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Enregistrement impossible");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Supprimer cette livraison ?")) return;
    try {
      await api.delete(`/admin/stores/deliveries/${id}`);
      toast.success("Livraison supprimée");
      await reload();
    } catch {
      toast.error("Suppression impossible");
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-bark-soft">
          Historique des livraisons. Marquer « Livrée » ajoute automatiquement les quantités au stock du magasin.
        </p>
        {!open && (
          <button
            data-testid="delivery-new-button"
            onClick={startNew}
            className="flex items-center gap-2 rounded-full bg-amber-deep px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-amber-deep/90"
          >
            <Plus size={16} /> Nouvelle livraison
          </button>
        )}
      </div>

      {open && (
        <div data-testid="delivery-form" className="rounded-2xl border border-amber-deep/30 bg-white p-5 shadow-sm">
          <div className="mb-4 flex items-center justify-between">
            <p className="font-serif text-lg font-semibold text-bark">{editing ? "Modifier la livraison" : "Nouvelle livraison"}</p>
            <button onClick={() => { setOpen(false); resetForm(); }} className="rounded-full p-1.5 text-stone-mute hover:bg-sand hover:text-bark" aria-label="Fermer">
              <X size={18} />
            </button>
          </div>
          <div className="flex flex-col gap-3 sm:flex-row">
            <label className="text-xs font-medium uppercase tracking-wider text-stone-mute">
              Date
              <input
                data-testid="delivery-date"
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                className="mt-1 block w-full rounded-xl border border-amber-800/20 px-3 py-2.5 text-sm text-bark outline-none focus:border-amber-deep"
              />
            </label>
            <label className="text-xs font-medium uppercase tracking-wider text-stone-mute">
              Statut
              <select
                data-testid="delivery-status"
                value={status}
                onChange={(e) => setStatus(e.target.value)}
                className="mt-1 block w-full rounded-xl border border-amber-800/20 px-3 py-2.5 text-sm text-bark outline-none focus:border-amber-deep"
              >
                <option value="prevue">Prévue</option>
                <option value="livree">Livrée (ajoute au stock)</option>
              </select>
            </label>
          </div>

          {/* Articles */}
          <div className="mt-4 rounded-xl bg-sand/50 p-4">
            <div className="flex flex-col gap-2 sm:flex-row sm:items-end">
              <label className="flex-1 text-xs font-medium uppercase tracking-wider text-stone-mute">
                Produit
                <select
                  data-testid="delivery-pick-product"
                  value={pickId}
                  onChange={(e) => setPickId(e.target.value)}
                  className="mt-1 block w-full rounded-xl border border-amber-800/20 bg-white px-3 py-2.5 text-sm text-bark outline-none focus:border-amber-deep"
                >
                  <option value="">Choisir un produit…</option>
                  {distributable.map((c) => (
                    <option key={c.product_id} value={c.product_id}>{c.weight ? `${c.name} - ${c.weight}` : c.name}</option>
                  ))}
                </select>
              </label>
              <label className="text-xs font-medium uppercase tracking-wider text-stone-mute">
                Quantité
                <input
                  data-testid="delivery-pick-qty"
                  value={pickQty}
                  onChange={(e) => setPickQty(e.target.value.replace(/[^0-9]/g, ""))}
                  inputMode="numeric"
                  className="mt-1 block w-24 rounded-xl border border-amber-800/20 px-3 py-2.5 text-sm text-bark outline-none focus:border-amber-deep"
                />
              </label>
              <button
                data-testid="delivery-add-item"
                onClick={addItem}
                className="flex items-center justify-center gap-1.5 rounded-full border border-amber-deep bg-white px-4 py-2.5 text-sm font-semibold text-amber-deep transition-colors hover:bg-amber-deep hover:text-white"
              >
                <Plus size={15} /> Ajouter
              </button>
            </div>

            {items.length > 0 && (
              <ul className="mt-3 space-y-1.5">
                {items.map((it) => (
                  <li key={it.product_id} data-testid={`delivery-item-${it.product_id}`} className="flex items-center justify-between gap-3 rounded-lg bg-white px-3 py-2 text-sm">
                    <span className="min-w-0 flex-1 truncate text-bark">{nameOf(it.product_id)}</span>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-stone-mute">Qté</span>
                      <input
                        data-testid={`delivery-item-qty-${it.product_id}`}
                        value={it.qty}
                        onChange={(e) => setItemQty(it.product_id, e.target.value)}
                        inputMode="numeric"
                        className="w-16 rounded-lg border border-amber-800/20 px-2 py-1.5 text-center text-sm text-bark outline-none focus:border-amber-deep"
                      />
                      <button data-testid={`delivery-item-remove-${it.product_id}`} onClick={() => setItems((its) => its.filter((i) => i.product_id !== it.product_id))} className="text-stone-mute hover:text-terra" aria-label="Retirer">
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <label className="mt-4 block text-xs font-medium uppercase tracking-wider text-stone-mute">
            Note (facultatif)
            <input
              data-testid="delivery-note"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              className="mt-1 w-full rounded-xl border border-amber-800/20 px-3 py-2.5 text-sm text-bark outline-none focus:border-amber-deep"
            />
          </label>

          <div className="mt-4 flex justify-end">
            <button
              data-testid="delivery-submit"
              onClick={submit}
              disabled={busy}
              className="flex items-center gap-2 rounded-full bg-amber-deep px-6 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-amber-deep/90 disabled:opacity-60"
            >
              {busy ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />} Enregistrer la livraison
            </button>
          </div>
        </div>
      )}

      {deliveries.length === 0 ? (
        <div data-testid="deliveries-empty" className="rounded-2xl border border-amber-800/15 bg-white p-10 text-center">
          <CalendarClock size={30} className="mx-auto text-stone-mute" />
          <p className="mt-3 font-serif text-xl text-bark">Aucune livraison enregistrée</p>
          <p className="mt-1 text-sm text-bark-soft">Ajoutez une livraison pour suivre l'historique de ce magasin.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {deliveries.map((d) => (
            <div key={d.delivery_id} data-testid={`delivery-card-${d.delivery_id}`} className="rounded-2xl border border-amber-800/15 bg-white p-5 shadow-sm">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="flex items-center gap-3">
                  <span className="font-serif text-lg font-semibold text-bark">{fmtDate(d.date)}</span>
                  <span className={`rounded-full px-3 py-1 text-xs font-semibold ${STATUS[d.status]?.cls || "bg-sand text-bark-soft"}`}>
                    {STATUS[d.status]?.label || d.status}
                  </span>
                </div>
                <div className="flex gap-1">
                  <button data-testid={`delivery-edit-${d.delivery_id}`} onClick={() => startEdit(d)} className="rounded-full px-3 py-1.5 text-xs font-medium text-amber-deep hover:bg-amber-deep/10">Modifier</button>
                  <button data-testid={`delivery-delete-${d.delivery_id}`} onClick={() => remove(d.delivery_id)} className="rounded-full p-1.5 text-stone-mute hover:bg-terra/10 hover:text-terra" aria-label="Supprimer">
                    <Trash2 size={15} />
                  </button>
                </div>
              </div>
              <ul className="mt-3 flex flex-wrap gap-2">
                {(d.items || []).map((it) => (
                  <li key={it.product_id} className="rounded-lg bg-sand/60 px-3 py-1 text-sm text-bark">
                    {nameOf(it.product_id, it.name)} <span className="text-stone-mute">×{it.qty}</span>
                  </li>
                ))}
              </ul>
              {d.note && <p className="mt-2 text-sm text-bark-soft">{d.note}</p>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
